import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import './PortalHeader.less';
import { PortalActionsMenu, IPortalActionLinkItem } from './PortalActionsMenu';
import { PortalSearchBox, IPortalSearchBoxProps } from './PortalSearchBox';
import { usePortalResize } from 'hooks/usePortalResize';
import { usePortalStore, getActions } from 'domains/portal/PortalStore';
import { getMaxZIndex } from 'utils/DomUtils';
import { Icon } from 'components/icons/Icon';

export interface IPortalHeaderProps {
  isDebuggingEnabled?: boolean;
  showSearchBox?: boolean;
  showCurrentFolder?: boolean;
  currentFolderName?: string;
  showNotifications?: boolean;
  slogan: string;
  onNotificationClick?: () => void;
  actionsProvider: () => IPortalActionLinkItem[] | Promise<IPortalActionLinkItem[]>;
  userDisplayName: string;
  timeZone?: string;
  searchProps?: Omit<IPortalSearchBoxProps, 'isCollapsed'>;
  notificationReceived?: boolean;
  onNotificationButtonReady?: (buttonElement: HTMLElement | null) => void;
  notificationsActive?: boolean;
  isMaintenanceModeEnabled?: boolean;
  showGlobalDebuggerIndicator: boolean;
}

/**
 * Header which displays on the top on the User Portal and Studio and contains Company Logo, Slogan and some actions
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function PortalHeader({
  slogan,
  userDisplayName,
  isDebuggingEnabled,
  showCurrentFolder,
  currentFolderName,
  showSearchBox,
  showNotifications,
  notificationReceived,
  onNotificationClick,
  actionsProvider,
  timeZone,
  searchProps,
  onNotificationButtonReady,
  notificationsActive,
  isMaintenanceModeEnabled,
  showGlobalDebuggerIndicator,
}: IPortalHeaderProps) {
  const { togglePortalActionsMenu } = usePortalStore(getActions);
  const isActionsOpened = usePortalStore(state => state.portalActionsOpen);
  const actionsContainer = useRef<HTMLButtonElement>(null);
  const headerElement = useRef<HTMLButtonElement>(null);
  const [actions, setActions] = useState<IPortalActionLinkItem[]>();
  const [isSearchCollapsed, setSearchCollapsibility] = useState(false);
  const [loadingActions, setLoadingActions] = useState(false);
  const [notificationZIndex, setNotificationZIndex] = useState<number>();

  const updateActions = useCallback(async () => {
    setLoadingActions(true);
    const portalActions = await actionsProvider();
    setLoadingActions(false);
    setActions(portalActions);
  }, [actionsProvider]);

  useEffect(() => {
    if (isActionsOpened) updateActions();
  }, [updateActions, isActionsOpened]);

  useEffect(() => {
    // manage z-index when notifications are showing
    // would it be better for the icon to be the callout caret, so to speak? child content of the callout?
    if (notificationsActive) {
      setNotificationZIndex(getMaxZIndex());
    } else {
      setNotificationZIndex(undefined);
    }
  }, [notificationsActive]);

  const updateSearchBox = useCallback(() => {
    if (headerElement.current) {
      //TODO alex: need to create constants for supported dimensions
      const isCollapsed = headerElement.current.clientWidth < 991;
      setSearchCollapsibility(isCollapsed);
    }
  }, []);

  usePortalResize(updateSearchBox);

  const getModeClassNames = classNames({
    'dp-portal-header__debug': isDebuggingEnabled,
    'dp-portal-header__maintenance': isMaintenanceModeEnabled,
  });

  const getPortalHeaderRightClassName = (showGlobalDebuggerIndicator?: boolean) =>
    classNames('dp-portal-header__right', {
      'dp-portal-header__right--with-debugger-running': showGlobalDebuggerIndicator,
    });

  const getModeTexts = useMemo(() => {
    if (isMaintenanceModeEnabled && isDebuggingEnabled) return '[Maintenance Mode, Debug Mode]';
    else if (isMaintenanceModeEnabled) return '[Maintenance Mode]';
    else if (isDebuggingEnabled) return '[Debug Mode]';
  }, [isDebuggingEnabled, isMaintenanceModeEnabled]);

  const getAreaLabelText = useMemo(() => {
    if (isMaintenanceModeEnabled && isDebuggingEnabled) return 'Maintenance and Debug modes are enabled';
    else if (isMaintenanceModeEnabled) return 'Maintenance mode is enabled';
    else if (isDebuggingEnabled) return 'Debug mode is enabled';
  }, [isDebuggingEnabled, isMaintenanceModeEnabled]);

  return (
    <header className="dp-portal-header" ref={headerElement}>
      <div className={getLeftClassName(!!(showCurrentFolder && currentFolderName))}>
        <div className="dp-portal-header__logo-text">
          <span className="dp-portal-header__slogan">{slogan}</span>
          {(isMaintenanceModeEnabled || isDebuggingEnabled) && (
            <span className={getModeClassNames} aria-label={getAreaLabelText}>
              {getModeTexts}
            </span>
          )}
        </div>
      </div>
      <div className="dp-portal-header__center">
        {showCurrentFolder && currentFolderName && (
          <span className="dp-portal-header__current-folder" aria-label="current folder">
            {currentFolderName}
          </span>
        )}
      </div>
      <div className={getPortalHeaderRightClassName(showGlobalDebuggerIndicator)}>
        {showGlobalDebuggerIndicator && (
          <span className="dp-portal-header__global-debugger" aria-label="global debugger">
            <Icon className="dp-portal-header__global-debugger-icon" size="small" inline={true} />
            Global Debugger running
          </span>
        )}
        <ul className="dp-portal-header__menu">
          {showSearchBox && searchProps && <PortalSearchBox {...searchProps} isCollapsed={isSearchCollapsed} />}
          <li
            className={classNames('dp-portal-header__menu-item', {
              'dp-portal-header__menu-item--active': isActionsOpened,
            })}
          >
            <button
              ref={actionsContainer}
              className="dp-portal-header__icon dp-portal-header__icon-account"
              aria-label="user actions"
              onClick={() => togglePortalActionsMenu(!isActionsOpened)}
            ></button>
            <PortalActionsMenu
              sourceRef={actionsContainer}
              isOpen={isActionsOpened}
              email={userDisplayName}
              actions={actions || []}
              timeZone={timeZone}
              onClose={() => togglePortalActionsMenu(false)}
              loading={loadingActions}
            />
          </li>
          {showNotifications && (
            <li
              className={getGetNotificationMenuItemClassName(notificationsActive)}
              style={{ zIndex: notificationZIndex }}
            >
              <button
                ref={ref => onNotificationButtonReady?.(ref)}
                className={getGetNotificationClassNames(notificationReceived)}
                aria-label="notifications"
                onClick={onNotificationClick}
              ></button>
              {notificationReceived && <i className="dp-portal-header__icon-notification-received" />}
            </li>
          )}
        </ul>
      </div>
    </header>
  );
}

function getGetNotificationClassNames(received?: boolean) {
  return classNames('dp-portal-header__icon dp-portal-header__icon-notification', {
    'dp-portal-header__icon-notification--recieved': received,
  });
}

function getGetNotificationMenuItemClassName(active?: boolean) {
  return classNames('dp-portal-header__menu-item dp-portal-header__menu-item-notifications', {
    'dp-portal-header__menu-item--active': active,
  });
}

function getLeftClassName(showingFolderTitle?: boolean) {
  return classNames('dp-portal-header__left', {
    'dp-portal-header__left--with-folder-name': showingFolderTitle,
  });
}
